import React, { useEffect, useRef, useState } from "react";
import { Loader2, Send, Sparkles, Lock, RotateCcw, Bot, User as UserIcon, ArrowUpRight, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";

/**
 * ScanChat — inline AI advisor at the bottom of a scan report.
 *
 * Props:
 *   scanId       string  — id of the scan we're chatting about
 *   scanReady    bool    — only allow chat once the scan has finished
 *
 * Behaviour:
 *   - Free workspaces see a paywall card with an Upgrade CTA.
 *   - Pro/Business workspaces see the chat UI: history + composer +
 *     suggested prompts (localised by the scan's language).
 *   - History persists per (scan, user) so each member has a private thread.
 */
export default function ScanChat({ scanId, scanReady }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);   // { allowed, plan, suggested_prompts, messages }
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [clearing, setClearing] = useState(false);
  const listRef = useRef(null);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/scans/${scanId}/chat`);
      setStatus(data);
    } catch (err) {
      // Don't toast on initial load failures — the report is still useful
      // without chat. Just leave status null so we render nothing.
      console.warn("scan chat load failed:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, [scanId]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Autoscroll on new message
  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [status?.messages?.length, sending]);

  const send = async (text) => {
    const body = (text ?? draft).trim();
    if (!body || sending) return;
    if (!scanReady) {
      toast.error("Wait for the scan to finish before chatting.");
      return;
    }
    setSending(true);
    // Optimistic user message
    const optimistic = {
      id: `tmp_${Date.now()}`,
      role: "user",
      content: body,
      created_at: new Date().toISOString(),
    };
    setStatus((s) => ({ ...s, messages: [...(s?.messages || []), optimistic] }));
    setDraft("");
    try {
      const { data } = await api.post(`/scans/${scanId}/chat`, { content: body });
      setStatus((s) => {
        const without = (s?.messages || []).filter((m) => m.id !== optimistic.id);
        return {
          ...s,
          messages: [...without, data.user_message, data.assistant_message],
          quota: data.quota || s?.quota,
        };
      });
    } catch (err) {
      // Roll back the optimistic message and surface the error.
      setStatus((s) => ({
        ...s,
        messages: (s?.messages || []).filter((m) => m.id !== optimistic.id),
      }));
      setDraft(body);
      // The new chat_quota_exceeded 402 ships a structured detail — handle
      // it inline so the user immediately understands why the message
      // bounced (and how to fix it).
      const code = err?.response?.data?.detail?.code;
      if (err?.response?.status === 402 && code === "chat_quota_exceeded") {
        const d = err.response.data.detail;
        toast.error("Monthly chat limit reached", {
          description: d.message,
          action: { label: "Upgrade", onClick: () => navigate("/billing") },
          duration: 12000,
        });
        // Reflect the exhausted quota in the UI immediately.
        setStatus((s) => ({
          ...s,
          quota: { used: d.used, limit: d.limit, remaining: 0,
                   period_start: s?.quota?.period_start || "" },
        }));
      } else {
        toast.error("Couldn't send message", { description: formatApiError(err) });
      }
    } finally {
      setSending(false);
    }
  };

  const clear = async () => {
    if (!status?.messages?.length) return;
    if (!window.confirm("Clear this conversation? Your chat history for this scan will be deleted.")) {
      return;
    }
    setClearing(true);
    try {
      await api.delete(`/scans/${scanId}/chat`);
      setStatus((s) => ({ ...s, messages: [] }));
      toast.success("Conversation cleared");
    } catch (err) {
      toast.error("Couldn't clear chat", { description: formatApiError(err) });
    } finally {
      setClearing(false);
    }
  };

  if (loading) {
    return (
      <div
        className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm flex items-center gap-2 text-sm text-slate-500"
        data-testid="scan-chat-loading"
      >
        <Loader2 className="h-4 w-4 animate-spin" /> Loading AI advisor…
      </div>
    );
  }

  if (!status) return null;   // initial load failed — keep the page usable

  if (!status.allowed) {
    return <ChatPaywall onUpgrade={() => navigate("/billing")} />;
  }

  const quota = status.quota || { used: 0, limit: 0, remaining: 0 };
  const pct = quota.limit > 0 ? Math.min(100, Math.round((quota.used / quota.limit) * 100)) : 0;
  // "Approaching limit" trigger: less than 20% remaining OR fewer than 30
  // messages left — whichever is more conservative.
  const lowQuota = quota.limit > 0 && (quota.remaining <= 30 || pct >= 80);
  const quotaExhausted = quota.limit > 0 && quota.remaining <= 0;
  const composerDisabled = !scanReady || sending || quotaExhausted;

  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden"
      data-testid="scan-chat"
    >
      <header className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-blue-600/10 text-blue-600">
          <Sparkles className="h-4 w-4" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900">Ask AgentFlow about this report</div>
          <div className="text-xs text-slate-500">
            Private to you. Answers reference the findings above — not your raw document.
          </div>
        </div>
        {status.messages?.length > 0 && (
          <button
            type="button"
            onClick={clear}
            disabled={clearing || sending}
            className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="scan-chat-clear"
          >
            {clearing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
            Clear
          </button>
        )}
      </header>

      {quota.limit > 0 && (
        <QuotaStrip
          used={quota.used}
          limit={quota.limit}
          remaining={quota.remaining}
          pct={pct}
          low={lowQuota}
          exhausted={quotaExhausted}
          plan={status.plan}
          onUpgrade={() => navigate("/billing")}
        />
      )}

      <div
        ref={listRef}
        className="px-6 py-5 space-y-4 max-h-[420px] overflow-y-auto bg-slate-50/60"
        data-testid="scan-chat-messages"
      >
        {(status.messages?.length || 0) === 0 ? (
          <EmptyState
            prompts={status.suggested_prompts || []}
            onPick={(p) => send(p)}
            disabled={composerDisabled}
          />
        ) : (
          status.messages.map((m) => <Bubble key={m.id} message={m} />)
        )}
        {sending && (
          <div className="flex items-start gap-3" data-testid="scan-chat-typing">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-blue-600 text-white shrink-0">
              <Bot className="h-3.5 w-3.5" />
            </span>
            <div className="rounded-2xl rounded-tl-sm bg-white border border-slate-200 px-4 py-3 text-sm text-slate-500 inline-flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking…
            </div>
          </div>
        )}
      </div>

      <form
        className="px-6 py-4 border-t border-slate-100 bg-white"
        onSubmit={(e) => { e.preventDefault(); send(); }}
        data-testid="scan-chat-form"
      >
        <div className="flex items-end gap-3">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                send();
              }
            }}
            disabled={composerDisabled}
            rows={2}
            placeholder={
              quotaExhausted
                ? "Monthly chat limit reached — upgrade to keep chatting."
                : scanReady
                ? "Ask anything about this report — e.g. \"How should I push back on §7?\""
                : "Chat unlocks when the scan finishes…"
            }
            className="flex-1 resize-none rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600/40 focus:border-blue-500 disabled:bg-slate-50 disabled:text-slate-400"
            data-testid="scan-chat-input"
            maxLength={2000}
          />
          <Button
            type="submit"
            disabled={composerDisabled || !draft.trim()}
            className="h-11 px-4 inline-flex items-center gap-1.5"
            data-testid="scan-chat-send"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Send
          </Button>
        </div>
        <div className="mt-2 text-[11px] text-slate-400 flex items-center justify-between">
          <span>⌘/Ctrl + Enter to send</span>
          <span>{draft.length}/2000</span>
        </div>
      </form>
    </div>
  );
}

function EmptyState({ prompts, onPick, disabled }) {
  return (
    <div className="text-center py-4" data-testid="scan-chat-empty">
      <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-600 text-white">
        <Sparkles className="h-5 w-5" />
      </div>
      <div className="mt-3 text-sm font-medium text-slate-900">
        Your private AI advisor for this report
      </div>
      <div className="text-xs text-slate-500 mt-1">
        Pick a starter — or ask anything you'd like to understand.
      </div>
      <div className="mt-4 flex flex-wrap gap-2 justify-center">
        {prompts.map((p) => (
          <button
            key={p}
            type="button"
            disabled={disabled}
            onClick={() => onPick(p)}
            className="text-xs text-blue-700 bg-white border border-blue-200 rounded-full px-3 py-1.5 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid={`scan-chat-suggested-${p.slice(0, 20)}`}
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  );
}

function Bubble({ message }) {
  const isUser = message.role === "user";
  return (
    <div
      className={`flex items-start gap-3 ${isUser ? "flex-row-reverse" : ""}`}
      data-testid={`scan-chat-msg-${message.role}`}
    >
      <span
        className={`inline-flex h-7 w-7 items-center justify-center rounded-lg shrink-0 ${
          isUser ? "bg-slate-900 text-white" : "bg-blue-600 text-white"
        }`}
      >
        {isUser ? <UserIcon className="h-3.5 w-3.5" /> : <Bot className="h-3.5 w-3.5" />}
      </span>
      <div
        className={`max-w-[78%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed ${
          isUser
            ? "bg-slate-900 text-white rounded-tr-sm"
            : "bg-white border border-slate-200 text-slate-800 rounded-tl-sm"
        }`}
      >
        {message.content}
      </div>
    </div>
  );
}

function QuotaStrip({ used, limit, remaining, pct, low, exhausted, plan, onUpgrade }) {
  // Three states: healthy (subtle), low (amber w/ upsell), exhausted (rose w/ upgrade)
  const tone = exhausted
    ? "bg-rose-50 border-rose-200 text-rose-900"
    : low
      ? "bg-amber-50 border-amber-200 text-amber-900"
      : "bg-slate-50 border-slate-100 text-slate-700";
  const bar = exhausted ? "bg-rose-500" : low ? "bg-amber-500" : "bg-blue-600";
  const ctaLabel = plan === "pro" ? "Upgrade to Business" : "Upgrade plan";

  return (
    <div
      className={`px-6 py-3 border-b text-xs ${tone}`}
      data-testid="scan-chat-quota"
    >
      <div className="flex items-center gap-3">
        {(low || exhausted) && (
          <AlertCircle className={`h-3.5 w-3.5 shrink-0 ${exhausted ? "text-rose-600" : "text-amber-600"}`} />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <span className="font-medium">
              {exhausted
                ? "Monthly chat limit reached"
                : low
                  ? `Only ${remaining} chat messages left this month`
                  : "Monthly chat usage"}
            </span>
            <span className="font-mono text-[11px] tabular-nums shrink-0" data-testid="scan-chat-quota-count">
              {used} / {limit}
            </span>
          </div>
          <div className="mt-1.5 h-1 w-full rounded-full bg-white/60 overflow-hidden">
            <div
              className={`h-full transition-all ${bar}`}
              style={{ width: `${Math.min(100, pct)}%` }}
              data-testid="scan-chat-quota-bar"
            />
          </div>
        </div>
        {(low || exhausted) && plan !== "business" && (
          <button
            type="button"
            onClick={onUpgrade}
            className={`shrink-0 inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-semibold transition ${
              exhausted
                ? "bg-rose-600 text-white hover:bg-rose-700"
                : "bg-amber-600 text-white hover:bg-amber-700"
            }`}
            data-testid="scan-chat-quota-upgrade"
          >
            {ctaLabel} <ArrowUpRight className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
}

function ChatPaywall({ onUpgrade }) {
  return (
    <div
      className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6 shadow-sm relative overflow-hidden"
      data-testid="scan-chat-paywall"
    >
      <div className="absolute -top-12 -right-12 h-48 w-48 rounded-full bg-blue-500/20 blur-3xl pointer-events-none" />
      <div className="flex items-start gap-4 relative">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-600/20 text-blue-300 shrink-0">
          <Sparkles className="h-5 w-5" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-[0.18em] font-semibold text-blue-300 inline-flex items-center gap-1.5">
            <Lock className="h-3 w-3" /> Pro feature
          </div>
          <h3 className="mt-2 font-display text-xl font-semibold leading-tight">
            Chat with this report
          </h3>
          <p className="mt-2 text-sm text-slate-300 max-w-xl">
            Ask AgentFlow anything about your scan: "Explain this clause",
            "Is this normal?", "How should I negotiate this?" — get
            plain-English answers grounded in your findings.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button
              onClick={onUpgrade}
              className="bg-blue-600 hover:bg-blue-500 text-white"
              data-testid="scan-chat-upgrade"
            >
              Upgrade to Pro
            </Button>
            <span className="text-xs text-slate-400">From $39/month · cancel anytime</span>
          </div>
        </div>
      </div>
    </div>
  );
}
